import React, { useState, useEffect } from "react"
import { makeStyles } from 'tss-react/mui';
import {
    Select,
    MenuItem,
    Button,
    TextField,
    FormControl,
    InputLabel,
} from "@mui/material"
import EventIcon from "@mui/icons-material/EventOutlined"
import AdminDialog, { DialogSwitchRow } from "./adminDialog"
import { setError } from "../store/features/error/errorSlice"
import { connect } from "react-redux"
import {
    updateHoliday,
    createHoliday,
} from "../utils/utils"
import { areObjectsEqual } from "../utils/common"

const mapDispatchToProps = {
    setError,
};

const useStyles = makeStyles()((theme) => ({
    name: {
        "& .MuiInputBase-input::placeholder": {
            fontSize: "0.85em",
            color: theme.palette.text.disabled,
            opacity: 1,
        },
    },
    date: {
        display: "flex",
        gap: theme.spacing(1.5),
    },
    month: {
        flex: 1,
    },
    day: {
        width: 96,
    },
    textButton: {
        textTransform: "none",
        color: theme.palette.text.secondary,
    },
    reset: {
        marginRight: "auto",
    },
    primary: {
        textTransform: "none",
        minWidth: 112,
        color: "#fff",
        backgroundColor: theme.palette.primary.light,
        boxShadow: "none",
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
            filter: "brightness(0.93)",
            boxShadow: "none",
        },
    },
}));

const Holiday = (props) => {
    const { classes, cx } = useStyles();
    const {
        open,
        setOpen,
        createMode,
        setCreateMode,
        holiday,
        setHoliday,
        refetchNotifications,
        session,
        setError,
    } = props;

    // let open = true
    // let holiday = props;
    // holiday = {
    //     "hid": 1,
    //     "name": "Thanks Giving",
    //     "month": "11",
    //     "day": "28",
    //     "active": "yes"
    // }
    const [currentHoliday, setCurrentHoliday] = useState(holiday)
    const [initHoliday, setInitHoliday] = useState(holiday)
    const [submitting, setSubmitting] = useState(false)

    if (createMode && !currentHoliday) {
        const hdInit =  {
            "name": "",
            "month": "01",
            "day": "01",
            "active": "no"
        }
        setCurrentHoliday(hdInit);
        setInitHoliday(hdInit);
    }

    const clearState = () => {
        setOpen(false)
        setCreateMode(false)
        setCurrentHoliday(null)
        setInitHoliday(null)
        setHoliday(null)
    }

    useEffect(() => {
        setCurrentHoliday(holiday)
        setInitHoliday(holiday)
    }, [holiday,open]);

    const handlePropertyChange = (field, value) => {
        setCurrentHoliday({
            ...currentHoliday,
            [field]: value,
        });
    };

    const handleUpdate = async () => {
        setSubmitting(true);
        try {
            let data = {
                hid: currentHoliday.hid,
                holidayDetails: {
                    name: currentHoliday.name,
                    month: currentHoliday.month,
                    day: currentHoliday.day,
                    active: currentHoliday.active,
                },
                token: session.token,
                server: session.lastServer,
            }
            await updateHoliday(data);
            setSubmitting(false);

            try {
                refetchNotifications()
                clearState()
                setError({
                    visible: true,
                    type: "success",
                    message: "Holiday updated."
                });
            } catch (error) {
                setSubmitting(false);
                setError({
                    visible: true,
                    type: "error",
                    message: "Failed to refetch Holidays. " + error,
                })
                setOpen(false)
            }
        } catch (error) {
            setSubmitting(false);
            setError({
                visible: true,
                type: "error",
                message: "Failed to update Holiday. " + error,
            })
        }
    };

    const handleCreate = async () => {
        setSubmitting(true);
        try {
            let data = {
                holidayDetails: {
                    name: currentHoliday.name,
                    month: currentHoliday.month,
                    day: currentHoliday.day,
                    active: currentHoliday.active,
                },
                token: session.token,
                server: session.lastServer,
            }
            await createHoliday(data);
            setSubmitting(false);

            try {
                refetchNotifications()
                clearState()
                setError({
                    visible: true,
                    type: "success",
                    message: "Holiday created!"
                });
            } catch (error) {
                setSubmitting(false);
                setError({
                    visible: true,
                    type: "error",
                    message: "Failed to refetch Holidays. " + error,
                })
                setOpen(false)
            }
        } catch (error) {
            setSubmitting(false);
            setError({
                visible: true,
                type: "error",
                message: "Failed to create Holiday. " + error,
            })
        }
    };

    const handleReset = () => {
        setCurrentHoliday(initHoliday)
    };

    if (!open || !currentHoliday)
        return null;

    const isModified = !areObjectsEqual(currentHoliday, initHoliday)
    const monthNames = {
        "01": "January",
        "02": "February",
        "03": "March",
        "04": "April",
        "05": "May",
        "06": "June",
        "07": "July",
        "08": "August",
        "09": "September",
        "10": "October",
        "11": "November",
        "12": "December"
    };
    const daysInMonth = {
        "01": 31,
        "02": 29,
        "03": 31,
        "04": 30,
        "05": 31,
        "06": 30,
        "07": 31,
        "08": 31,
        "09": 30,
        "10": 31,
        "11": 30,
        "12": 31
    };

    const renderDays = () => {
        const numDays = daysInMonth[currentHoliday.month] || 0; // Default to 0 days if no month is selected
        return Array.from({ length: numDays }, (_, i) => (
            <MenuItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                {i + 1}
            </MenuItem>
        ));
    };

    const nameMissing = !String(currentHoliday.name || "").trim();
    const menuProps = { slotProps: { paper: { style: { maxHeight: 280 } } } };

    return (
        <AdminDialog
            open={open}
            onClose={clearState}
            icon={<EventIcon />}
            title={createMode ? "New holiday" : "Edit holiday"}
            subtitle="A day of the year that repeats every year."
            footer={
                <>
                    <Button
                        onClick={handleReset}
                        disabled={submitting || !isModified}
                        className={cx(classes.textButton, classes.reset)}
                    >
                        Reset
                    </Button>
                    <Button onClick={clearState} className={classes.textButton}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.primary}
                        disabled={submitting || !isModified || nameMissing}
                        onClick={(createMode) ? handleCreate : handleUpdate}
                    >
                        {submitting
                            ? ((createMode) ? "Creating…" : "Updating…")
                            : ((createMode) ? "Create" : "Update")}
                    </Button>
                </>
            }
        >
            <TextField
                required
                autoFocus={createMode}
                variant="outlined"
                size="small"
                fullWidth
                label="Holiday name"
                placeholder="For example, Thanksgiving"
                className={classes.name}
                value={currentHoliday.name}
                onChange={(e) => handlePropertyChange('name', e.target.value)}
                // Red only once the name was there and got removed.
                error={nameMissing && isModified}
                slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { maxLength: 32 }
                }}
            />
            <div className={classes.date}>
                <FormControl size="small" className={classes.month}>
                    <InputLabel id="holiday-month-label">Month</InputLabel>
                    <Select
                        labelId="holiday-month-label"
                        label="Month"
                        value={currentHoliday.month}
                        onChange={(e) => handlePropertyChange('month', e.target.value)}
                        MenuProps={menuProps}
                    >
                        {Object.entries(monthNames)
                         .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // Sort keys numerically as strings
                         .map(([value, name]) => (
                            <MenuItem key={value} value={value}>
                                {name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                { currentHoliday.month && (
                    <FormControl size="small" className={classes.day}>
                        <InputLabel id="holiday-day-label">Day</InputLabel>
                        <Select
                            labelId="holiday-day-label"
                            label="Day"
                            value={currentHoliday.day}
                            onChange={(e) => handlePropertyChange('day', e.target.value)}
                            MenuProps={menuProps}
                        >
                            {renderDays()}
                        </Select>
                    </FormControl>
                )}
            </div>
            <DialogSwitchRow
                label="Active"
                checked={currentHoliday.active === "yes"}
                onChange={() => handlePropertyChange('active', currentHoliday.active === "yes" ? "no" : "yes")}
            />
        </AdminDialog>
    );
}

const mapStateToProps = (state) => {
    return {
        user: state.user,
        session: state.session,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Holiday);